import {
  ReactNode,
  useState,
  useRef,
  useEffect,
  memo,
  CSSProperties,
  startTransition,
} from 'react'

import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Text } from '@hhgtech/hhg-components/mantine'
import { theme } from '@hhgtech/hhg-components/misc'
import { List } from '@mantine/core'
import { useIntl } from 'react-intl'

import {
  StyledSources,
  StyledHeading,
  StyledExpandableContainer,
} from './index.styled'
const OpenIcon = CommonGAssets.getAssetPath('source-opened.svg')
const CloseIcon = CommonGAssets.getAssetPath('source-closed.svg')

export type Props = {
  className?: string
  sources?: string | string[]
  title?: ReactNode
  children?: ReactNode
}

const _Sources = ({ className, sources = '', title, children }: Props) => {
  const { formatMessage: f } = useIntl()
  const [expand, setExpand] = useState(false)
  const sourcesRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(100)
  useEffect(() => {
    setTimeout(() => {
      if (sourcesRef.current) {
        setContentHeight(sourcesRef.current.clientHeight)
      }
    }, 100)
  }, [Array.isArray(sources) ? sources.join('|') : sources])

  return (
    <StyledSources className={className}>
      <StyledHeading
        onClick={() => {
          startTransition(() => {
            setExpand((e) => !e)
          })
        }}
        tabIndex={0}
      >
        <Text size="c2" weight="bold" color={theme.colors.gray600}>
          <img
            src={expand ? CloseIcon : OpenIcon}
            alt=""
            loading="lazy"
            style={{
              verticalAlign: 'bottom',
              marginRight: 8,
            }}
          />
          {title || f({ id: 'articlePage.sourceCount' })}
        </Text>
      </StyledHeading>
      <StyledExpandableContainer
        data-expand={expand}
        style={
          {
            '--expand-height': `${contentHeight}px`,
          } as CSSProperties
        }
      >
        <div className="source-wrapper" ref={sourcesRef}>
          {children ? (
            children
          ) : (
            <>
              {Array.isArray(sources) && (
                <List
                  styles={{
                    itemIcon: {
                      marginRight: 0,
                    },
                  }}
                  spacing={12}
                  icon={
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="2" fill="#2D87F3" />
                    </svg>
                  }
                >
                  {sources.map((s) => (
                    <List.Item key={s}>
                      <div dangerouslySetInnerHTML={{ __html: s }}></div>
                    </List.Item>
                  ))}
                </List>
              )}
              {typeof sources === 'string' && (
                <div dangerouslySetInnerHTML={{ __html: sources }}></div>
              )}
            </>
          )}
        </div>
      </StyledExpandableContainer>
    </StyledSources>
  )
}

export const Sources = memo(_Sources, (prev, next) => {
  const prevSourceStr = Array.isArray(prev.sources)
    ? prev.sources.join('|')
    : prev.sources
  const nextSourceStr = Array.isArray(next.sources)
    ? next.sources.join('|')
    : next.sources
  return prevSourceStr === nextSourceStr && prev.title === next.title
})
